import { useState } from 'react';
import { toast } from 'sonner';
import { X, Upload, ClipboardPaste, Link2, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

const TABS = [
  { key: 'upload', label: 'Upload', icon: Upload, hint: 'Screenshot or PDF of the schedule. Works for any con.' },
  { key: 'paste', label: 'Paste text', icon: ClipboardPaste, hint: 'Copy the schedule text and paste it here.' },
  { key: 'link', label: 'From link', icon: Link2, hint: 'A schedule URL or an iCal (.ics) feed. JS-heavy pages may need a screenshot instead.' },
];

const INPUT = 'w-full bg-slate-950/50 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/40 transition-colors';

export default function ImportModal({ conId, onClose, onImported }) {
  const [tab, setTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState('');
  const [progress, setProgress] = useState(null);

  const submit = async () => {
    setWarn('');
    setProgress(null);
    const fd = new FormData();
    if (tab === 'upload') {
      if (!file) return toast.error('Choose a screenshot or PDF first');
      fd.append('file', file);
    } else if (tab === 'paste') {
      if (!text.trim()) return toast.error('Paste the schedule text first');
      fd.append('text', text);
    } else {
      if (!url.trim()) return toast.error('Enter a URL first');
      fd.append('url', url.trim());
    }
    setBusy(true);
    try {
      const start = await api.post(`/cons/${conId}/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jobId = start.data.job_id;
      if (!jobId) {
        setWarn('Could not start the import. Please try again.');
        return;
      }
      const began = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - began > 8 * 60 * 1000) {
          setWarn('This is taking longer than expected. Try a smaller screenshot.');
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
        let job;
        try {
          job = (await api.get(`/cons/${conId}/import/status/${jobId}`)).data;
        } catch {
          continue;
        }
        if (job.total_chunks) {
          setProgress({ total: job.total_chunks, done: job.done_chunks || 0, events: job.events_found || 0 });
        }
        if (job.status === 'done') {
          const n = job.event_count || 0;
          if (n === 0) {
            setWarn('No events were found. Try a clearer screenshot or paste the text.');
            return;
          }
          toast.success(`Imported ${n} event${n === 1 ? '' : 's'}`);
          onImported(job.events || []);
          return;
        }
        if (job.status === 'unreadable') {
          setWarn(job.message || 'Could not read that source.');
          return;
        }
        if (job.status === 'failed') {
          toast.error(job.message || 'Import failed');
          return;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const active = TABS.find((t) => t.key === tab);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" /> Import schedule
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          AI reads the schedule and pulls out the panels and shows. You pick what to attend next.
        </p>

        <div className="flex gap-2 mb-3">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); setWarn(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium border transition-colors ${
                tab === t.key
                  ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'
                  : 'border-white/10 text-slate-300 hover:bg-white/5'
              }`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mb-3">{active.hint}</p>

        {tab === 'upload' && (
          <label className="block border border-dashed border-white/15 rounded-xl p-6 text-center cursor-pointer hover:border-cyan-500/40 transition-colors">
            <input type="file" accept="image/*,application/pdf,.ics" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <Upload className="w-6 h-6 mx-auto text-slate-500 mb-2" />
            <p className="text-sm text-slate-300">{file ? file.name : 'Tap to choose a screenshot or PDF'}</p>
          </label>
        )}
        {tab === 'paste' && (
          <textarea className={`${INPUT} h-40 resize-none`} placeholder="Paste the schedule here…"
            value={text} onChange={(e) => setText(e.target.value)} />
        )}
        {tab === 'link' && (
          <input className={INPUT} placeholder="https://… schedule page or .ics feed"
            value={url} onChange={(e) => setUrl(e.target.value)} />
        )}

        {warn && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{warn}</span>
          </div>
        )}

        {busy && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
              <span>
                {!progress
                  ? 'Preparing schedule…'
                  : progress.done === 0
                    ? `Reading ${progress.total} section${progress.total === 1 ? '' : 's'}…`
                    : `Reading schedule… ${Math.round((progress.done / progress.total) * 100)}%`}
              </span>
              {progress?.events > 0 && (
                <span>{progress.events} event{progress.events === 1 ? '' : 's'} so far</span>
              )}
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              {progress?.done > 0 ? (
                <div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-400 transition-all duration-500"
                  style={{ width: `${Math.max(6, Math.round((progress.done / progress.total) * 100))}%` }} />
              ) : (
                <div className="h-full w-2/5 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-400 animate-pulse" />
              )}
            </div>
            {(!progress || progress.done === 0) && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                Large PDFs can take a couple of minutes — you can leave this open.
              </p>
            )}
          </div>
        )}

        <button onClick={submit} disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4">
          {busy
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading schedule…</>
            : <><Sparkles className="w-4 h-4" /> Import</>}
        </button>
      </div>
    </div>
  );
}
